import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'
import { Box, Flex, Text } from '@fundamentals'
import { LayerItem } from './LayerItem'
import { Virtuoso } from 'react-virtuoso'
import { Button } from '@common/material'
import { LayerForm } from './LayerForm'
import {
  useDeleteProjectMapLayer,
  useUpdateProjectMapLayer,
  useUpdateProjectMapLayersOrder,
} from '@core/react-query/features/projects/maps'
import { useNavigationContext } from '@common/hooks'
import axios from 'axios'
import fileDownload from 'js-file-download'
import {
  PMConfiguratorMode,
  useProjectMapConfigurator,
} from '@modules/geolocation'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import ConfirmModal from '@common/modal/ConfirmModal'
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { DraggableContainer, DraggableItemVirtualised } from '@common/dnd'

type LayersTabPanelProps = {
  active: boolean
}

// Form-safe type that excludes circular GeoJSON references
type ProjectMapLayerFormData = Omit<ProjectMapLayer, 'localGeoJSON'> & {
  fieldId: string
}

type LayersFormData = {
  layers: ProjectMapLayerFormData[]
  activeLayer: ProjectMapLayerFormData | null
}

export type LayersTabPanelRef = {
  updateLayers: (layers: ProjectMapLayer[]) => void
}

export const LayersTabPanel = forwardRef<
  LayersTabPanelRef,
  LayersTabPanelProps
>(({ active }, ref) => {
  const { projectId } = useNavigationContext()
  const { showModal, hideModal } = useModal()
  const toast = useToast()
  const updateProjectMapLayersOrderMutation = useUpdateProjectMapLayersOrder()

  // Form setup with react-hook-form
  const { control, reset, watch, setValue, getValues } =
    useForm<LayersFormData>({
      defaultValues: {
        layers: [],
        activeLayer: null,
      },
    })

  const { fields: layers, move } = useFieldArray({
    control,
    name: 'layers',
    keyName: 'fieldId', // Use different key to preserve original layer.id
  })

  // Watch form values
  const activeLayer = watch('activeLayer')

  // Backend-served tileset layers (synthetic ids, not DB rows): shown as
  // display-only rows below the editable list — the layer mutations
  // (edit/delete/download/reorder) only apply to persisted KMZ/KML layers.
  const [tilesetLayers, setTilesetLayers] = useState<ProjectMapLayer[]>([])

  // Expose update method to parent - parent controls when to update layers
  useImperativeHandle(ref, () => ({
    updateLayers: (layers: ProjectMapLayer[]) => {
      reset({
        layers: layers.filter((layer) => !layer.tilesetId),
        activeLayer: null,
      })
      setTilesetLayers(layers.filter((layer) => !!layer.tilesetId))
    },
  }))

  const {
    mode,
    setMode,
    setSelectedLayer,
    toggleLayerVisibilityOnMap,
    deleteLayerFromMap,
    zoomToLayerOnMap,
  } = useProjectMapConfigurator()

  const updateProjectMapLayerMutation = useUpdateProjectMapLayer()
  const deleteProjectMapLayerMutation = useDeleteProjectMapLayer()

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const layer = layers.find((field) => field.fieldId === event.active.id)
    setValue('activeLayer', layer)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    setValue('activeLayer', null)

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = layers.findIndex((field) => field.fieldId === active.id)
    const newIndex = layers.findIndex((field) => field.fieldId === over.id)
    move(oldIndex, newIndex)

    const layerIds = getValues('layers').map((layer) => Number(layer.id))

    updateProjectMapLayersOrderMutation.mutate(
      {
        projectId,
        layerIds,
      },
      {
        onSuccess: (data) => {
          // Sync form with new server data after reorder (tileset layers stay
          // out of the editable list — same partition as updateLayers)
          reset({
            layers: data.filter((layer) => !layer.tilesetId),
            activeLayer: null,
          })
          setTilesetLayers(data.filter((layer) => !!layer.tilesetId))
          toast.success(localizedStrings.orderChanged)
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )
  }

  const handleOnZoomToFeatures = (layer: ProjectMapLayer) => {
    zoomToLayerOnMap(layer)
  }

  const handleOnShowHideSource = async (layer: ProjectMapLayer) => {
    await updateProjectMapLayerMutation.mutateAsync({
      projectId: projectId,
      layerId: Number(layer.id),
      visible: !layer.visible,
    })

    toggleLayerVisibilityOnMap(layer)
  }

  const handleOnEditLayer = (layer: ProjectMapLayer) => {
    setSelectedLayer(layer)
    setMode(PMConfiguratorMode.LAYER_FORM)
  }

  const handleOnDeleteLayer = (layer: ProjectMapLayer) => {
    showModal(
      <ConfirmModal
        title={localizedStrings.deleteLayer}
        confirmText={localizedStrings.delete}
        onConfirm={async () => {
          await deleteProjectMapLayerMutation.mutateAsync({
            projectId,
            layerId: Number(layer.id),
          })

          deleteLayerFromMap(layer)

          hideModal()
        }}
      >
        {localizedStrings.confirmDeleteLayer}
      </ConfirmModal>,
    )
  }

  const handleOnDownloadLayer = async (layer: ProjectMapLayer) => {
    const response = await axios.get(layer.fileDownloadUrl, {
      responseType: 'blob',
    })

    const fileName = layer.name + `.${layer.type.toLowerCase()}`

    fileDownload(response.data, fileName)
  }

  return (
    <Flex
      hidden={!active}
      mt={2}
      flex={1}
      flexDirection='column'
      overflow='hidden'
    >
      {mode === 'LAYER_FORM' && (
        <LayerForm
          onDismiss={() => {
            setMode(PMConfiguratorMode.LAYER_LIST)
            setSelectedLayer(null)
          }}
        />
      )}

      {mode === 'LAYER_LIST' && (
        <>
          <DraggableContainer
            list={layers.map((field) => field.fieldId)}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            renderDragOverlay={(activeId) => {
              return activeLayer ? (
                <LayerItem
                  key={activeLayer.fieldId}
                  layer={activeLayer}
                  chip={{ label: activeLayer.type }}
                  isLoading={false}
                  sx={{ opacity: 0.9, cursor: 'grabbing' }}
                />
              ) : null
            }}
          >
            <Virtuoso
              data={layers}
              components={{
                EmptyPlaceholder: () => (
                  <Box
                    sx={{
                      textAlign: 'center',
                      marginY: 3,
                    }}
                  >
                    <Text variant='body1' color={'textSecondary'}>
                      {localizedStrings.noLayersAddedToDisplay}
                    </Text>
                  </Box>
                ),
                Footer: () => (
                  <>
                    {tilesetLayers.map((layer) => (
                      <LayerItem
                        key={layer.id}
                        layer={layer}
                        chip={{ label: localizedStrings.dxfTileset }}
                      />
                    ))}
                    <Button
                      variant='outlined'
                      fullWidth
                      onClick={() => setMode(PMConfiguratorMode.LAYER_FORM)}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {localizedStrings.addLayer}
                    </Button>
                  </>
                ),
              }}
              itemContent={(index, field) => (
                <DraggableItemVirtualised
                  key={field.fieldId}
                  id={field.fieldId}
                >
                  {(dragHandleProps) => (
                    <LayerItem
                      layer={field}
                      chip={{ label: field.type }}
                      onEdit={handleOnEditLayer}
                      onShowHide={handleOnShowHideSource}
                      onDelete={handleOnDeleteLayer}
                      onZoomTo={handleOnZoomToFeatures}
                      onDownload={handleOnDownloadLayer}
                      isLoading={
                        updateProjectMapLayerMutation.isLoading &&
                        updateProjectMapLayerMutation.variables?.layerId ===
                          Number(field.id)
                      }
                      dragHandleProps={{
                        ...dragHandleProps.attributes,
                        ...dragHandleProps.listeners,
                      }}
                    />
                  )}
                </DraggableItemVirtualised>
              )}
            />
          </DraggableContainer>
        </>
      )}
    </Flex>
  )
})

LayersTabPanel.displayName = 'LayersTabPanel'
